import React from 'react';
import classnames from 'classnames';
import { useIntl } from 'umi';

import SectionHeader from '../SectionHeader';
import ShortcutsGrid from './components/ShortcutsGrid';
import EditShortcutsModal from './components/EditShortcutsModal';
import AnalyseUrlDataModal from './components/AnalyseUrlDataModal';
import useMyShortcutsController from './_hooks/useMyShortcutsController';

export interface IMyShortcutsProps extends IBaseProps {
  title?: string;
  actionText?: string;
  maxCount?: number;
}

const MyShortcuts: React.FC<IMyShortcutsProps> = (props) => {
  const { style, className, title, actionText, maxCount = 6 } = props;
  const { formatMessage } = useIntl();
  const titleText =
    title ||
    formatMessage({
      id: 'dbh_url_collection_shortcuts_title',
      defaultMessage: 'My Shortcuts',
    });
  const actionTextValue =
    actionText ||
    formatMessage({
      id: 'dbh_url_collection_edit',
      defaultMessage: 'Edit',
    });
  const { viewState, editModalProps, analyseModalProps, handlers } = useMyShortcutsController({
    maxCount,
  });

  return (
    <>
      <div style={style} className={classnames('dbh-my-shortcuts', 'dbh-url-collection-section', className)}>
        <SectionHeader
          title={titleText}
          actionText={actionTextValue}
          showAction={viewState.showAction}
          onActionClick={handlers.handleOpenEditShortcutsModal}
        />
        <ShortcutsGrid
          loading={viewState.shortcutsLoading}
          items={viewState.currentShortcuts}
          showAddCard={viewState.showAddCard}
          onItemClick={handlers.handleShortcutClick}
          onAddClick={handlers.handleOpenEditShortcutsModal}
        />
      </div>
      {editModalProps.visible && (
        <EditShortcutsModal
          visible={editModalProps.visible}
          items={editModalProps.items}
          initialItems={editModalProps.initialItems}
          maxCount={maxCount}
          loading={editModalProps.loading}
          disabledClose={editModalProps.disabledClose}
          title={formatMessage({
            id: 'dbh_url_collection_edit_modal_title',
            defaultMessage: 'Edit My Shortcuts',
          })}
          shortcutsTitle={titleText}
          addText={formatMessage({
            id: 'dbh_url_collection_add_shortcut',
            defaultMessage: 'Add Shortcut',
          })}
          confirmText={formatMessage({ id: 'dbh_confirm' })}
          cancelText={formatMessage({ id: 'dbh_cancel' })}
          maxCountMessage={formatMessage({
            id: 'dbh_url_collection_max_count_message',
            defaultMessage: 'Maximum limit of 6 favorites reached. Remove an item to add a new one.',
          })}
          nameEmptyMessage={formatMessage({
            id: 'dbh_url_collection_name_required',
            defaultMessage: 'Name is required.',
          })}
          nameTooLongMessage={formatMessage({
            id: 'dbh_url_collection_name_too_long',
            defaultMessage: 'Name must be 80 characters or fewer.',
          })}
          duplicateNameMessage={formatMessage({
            id: 'dbh_url_collection_duplicate_name',
            defaultMessage: 'This name has already been used.',
          })}
          duplicateUrlMessage={formatMessage({
            id: 'dbh_url_collection_duplicate_url',
            defaultMessage: 'This page has already been added.',
          })}
          onAdd={editModalProps.onAdd}
          onCancel={editModalProps.onCancel}
          onItemsChange={editModalProps.onItemsChange}
          onItemClick={editModalProps.onItemClick}
          onConfirm={editModalProps.onConfirm}
        />
      )}
      {analyseModalProps.visible && (
        <AnalyseUrlDataModal
          visible={analyseModalProps.visible}
          url={analyseModalProps.url}
          pageName={analyseModalProps.pageName}
          analysisResult={analyseModalProps.analysisResult}
          importLoading={analyseModalProps.importLoading}
          confirmLoading={analyseModalProps.confirmLoading}
          shortcutImportErrorText={analyseModalProps.shortcutImportErrorText}
          onUrlChange={analyseModalProps.onUrlChange}
          onPageNameChange={analyseModalProps.onPageNameChange}
          onImport={analyseModalProps.onImport}
          onConfirm={analyseModalProps.onConfirm}
          onClose={analyseModalProps.onClose}
        />
      )}
    </>
  );
};

export default MyShortcuts;
